import React from 'react';
import { useTranslation } from 'react-i18next';
import EventStatusBadge, { type EventStatus } from './EventStatusBadge';
import './Calendar.css';

export interface CalendarEvent {
  _id: string;
  name: string;
  description: string;
  location?: string;
  startTime: string;
  endTime: string;
  color?: string; // Optional color for the event
  attending?: boolean | null; // Attendance status: true=Yes, false=No, null=Maybe
  // Invited but not on the roster (D17): no attendance, nothing owed.
  available?: boolean;
  member?: {
    _id: string;
    name: string;
    color: string;
  };
  // Attendance counts for admin view
  attendingCount?: number;
  totalMembers?: number;
  // Payment status for admin view
  price?: number;
  paidCount?: number;
  partialCount?: number;
  unpaidCount?: number;
  // Per-occurrence status (migration 0056). Absent on legacy payloads.
  status?: EventStatus;
  // Access control
  private?: boolean;
  // Template reference (null = manual event)
  template?: { _id: string; name: string } | string | null;
  // Assignment
  members?: string[];
  groups?: string[];
  defaultAttendance?: boolean | null;
  responsible?: string | { _id: string; name?: string; aboutMe?: string; photoUrl?: string | null };
  // Public/open event settings
  isOpen?: boolean;
  capacity?: number;
  publicSlug?: string;
  // Extended content
  extendedDescription?: string;
  gallery?: string[];
  colorSettings?: { primaryColor?: string; secondaryColor?: string };
}

interface CalendarProps {
  year: number;
  month: number; // 0-indexed (0 = January)
  events: CalendarEvent[];
  onDayClick: (date: Date, dayEvents: CalendarEvent[]) => void;
  onEventClick: (event: CalendarEvent) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  filterContent?: React.ReactNode; // Optional filter content for header
}

// Days starting with Monday (European format)
const DAYS_OF_WEEK_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december'
];

const Calendar: React.FC<CalendarProps> = ({
  year,
  month,
  events,
  onDayClick,
  onEventClick,
  onPrevMonth,
  onNextMonth,
  onToday,
  filterContent,
}) => {
  const { t } = useTranslation();
  // Get the first day of the month and number of days
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  // Convert Sunday=0 based to Monday=0 based (European format)
  const dayOfWeek = firstDayOfMonth.getDay();
  const startingDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  // Get today's date for highlighting
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  // Build calendar grid
  const calendarDays: (number | null)[] = [];

  // Add empty cells for days before the first of the month
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }

  // Add the days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // Add empty cells at the end to complete the last week
  const remainingCells = (7 - (calendarDays.length % 7)) % 7;
  for (let i = 0; i < remainingCells; i++) {
    calendarDays.push(null);
  }

  // Get events for a specific day
  const getEventsForDay = (day: number): CalendarEvent[] => {
    const dayStart = new Date(year, month, day, 0, 0, 0);
    const dayEnd = new Date(year, month, day, 23, 59, 59);

    return events.filter((event) => {
      const eventStart = new Date(event.startTime);
      const eventEnd = new Date(event.endTime);
      // Event overlaps with this day if it starts before day ends AND ends after day starts
      return eventStart <= dayEnd && eventEnd >= dayStart;
    });
  };

  const handleDayClick = (day: number) => {
    const clickedDate = new Date(year, month, day, 12, 0, 0);
    const dayEvents = getEventsForDay(day);
    onDayClick(clickedDate, dayEvents);
  };

  const handleEventClick = (e: React.MouseEvent, event: CalendarEvent) => {
    e.stopPropagation(); // Prevent day click from firing
    onEventClick(event);
  };

  return (
    <div className="calendar">
      <div className="calendar-header">
        <div className="calendar-header-left">
          <button className="calendar-nav-btn" onClick={onPrevMonth}>
            &lt;
          </button>
          {filterContent && (
            <div className="calendar-filter">
              {filterContent}
            </div>
          )}
        </div>
        <div className="calendar-header-center">
          <h2 className="calendar-title">
            {t(`calendar.months.${MONTH_KEYS[month]}`)} {year}
          </h2>
          <button className="calendar-today-btn" onClick={onToday}>
            {t('calendar.today')}
          </button>
        </div>
        <div className="calendar-header-right">
          <button className="calendar-nav-btn" onClick={onNextMonth}>
            &gt;
          </button>
        </div>
      </div>

      <div className="calendar-grid">
        {/* Day headers (desktop only) */}
        {DAYS_OF_WEEK_KEYS.map((dayKey) => (
          <div key={dayKey} className="calendar-day-header desktop-only">
            {t(`calendar.days.${dayKey}`)}
          </div>
        ))}

        {/* Calendar days */}
        {calendarDays.map((day, index) => {
          const dayOfWeekIndex = index % 7;
          const isMonday = dayOfWeekIndex === 0;
          const weekNumber = Math.floor(index / 7) + 1;
          const dayName = t(`calendar.days.${DAYS_OF_WEEK_KEYS[dayOfWeekIndex]}`);

          const dayEvents = day !== null ? getEventsForDay(day) : [];
          const isToday = day !== null && isCurrentMonth && today.getDate() === day;

          return (
            <React.Fragment key={index}>
              {isMonday && (
                <div className="calendar-week-separator mobile-only">
                  {t('calendar.week')} {weekNumber}
                </div>
              )}
              <div
                className={`calendar-day ${day === null ? 'empty' : ''} ${isToday ? 'today' : ''} ${dayEvents.length > 0 ? 'has-events' : ''}`}
                onClick={() => day !== null && handleDayClick(day)}
              >
                <div className="calendar-day-header-info">
                  <span className="calendar-day-number">{day}</span>
                  {day !== null && (
                    <span className="calendar-day-name mobile-only">
                      {dayName}
                    </span>
                  )}
                </div>
                <div className="calendar-day-events">
                  {dayEvents.slice(0, 3).map((event) => {
                    const eventColor = event.member?.color || event.color;
                    const eventLabel = event.member
                      ? `${event.name} - ${event.member.name}`
                      : event.name;
                    // Only apply attendance styling for user view (when event has member)
                    const hasAttendanceStatus = event.member !== undefined && !event.available;
                    const attendanceClass = hasAttendanceStatus
                      ? (event.attending === true ? 'attending' : event.attending === false ? 'not-attending' : 'maybe')
                      : '';
                    const indicatorClass = event.attending === true ? 'yes' : event.attending === false ? 'no' : 'maybe';
                    const indicatorSymbol = event.attending === true ? '✓' : event.attending === false ? '✗' : '?';

                    // Admin view attendance counter
                    const totalMembers = event.totalMembers ?? 0;
                    const hasAttendanceCounts = totalMembers > 0;
                    const attendancePercentage = hasAttendanceCounts
                      ? (event.attendingCount || 0) / totalMembers
                      : 0;
                    let attendanceCounterClass = '';
                    if (hasAttendanceCounts) {
                      if (attendancePercentage === 1) {
                        attendanceCounterClass = 'attendance-full'; // Green - 100%
                      } else if (attendancePercentage >= 0.7) {
                        attendanceCounterClass = 'attendance-high'; // Yellow - 70%+
                      } else if (attendancePercentage >= 0.3) {
                        attendanceCounterClass = 'attendance-medium'; // Orange - 30%+
                      } else {
                        attendanceCounterClass = 'attendance-low'; // Red - <30%
                      }
                    }

                    // Stripe colour: responsible/member colour, else brand orange.
                    // The tint is derived from --evc via color-mix in CSS; when
                    // there is no event colour we fall back to the brand orange
                    // stripe and leave --evc unset so no tint is applied.
                    const stripeColor = eventColor || 'var(--orange-primary)';

                    // Scheduled is the norm; badging every chip green would
                    // drown the two states that actually need attention.
                    const statusClass = event.status && event.status !== 'scheduled'
                      ? ` calendar-event--${event.status}`
                      : '';

                    return (
                      <div
                        key={event._id}
                        className={`calendar-event ${attendanceClass}${statusClass}${event.available ? ' calendar-event--available' : ''}`}
                        style={{
                          borderLeftColor: stripeColor,
                          ...(eventColor ? { '--evc': eventColor } as React.CSSProperties : {}),
                        }}
                        title={eventLabel}
                        onClick={(e) => handleEventClick(e, event)}
                      >
                        {hasAttendanceStatus && (
                          <span className={`attendance-indicator ${indicatorClass}`}>
                            {indicatorSymbol}
                          </span>
                        )}
                        <span className="calendar-event-name">{eventLabel}</span>
                        {event.available && (
                          <span className="calendar-event-available">{t('calendar.available')}</span>
                        )}
                        {event.status && event.status !== 'scheduled' && (
                          <EventStatusBadge status={event.status} compact />
                        )}
                        {hasAttendanceCounts && !event.member && (
                          <span className={`attendance-counter ${attendanceCounterClass}`}>
                            <i className="fas fa-user" aria-hidden="true"></i>
                            {event.attendingCount || 0}/{totalMembers}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {dayEvents.length > 3 && (
                    <div className="calendar-event-more">
                      +{dayEvents.length - 3} {t('calendar.more')}
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
